import React, { useState, useEffect } from 'react';
import { IcoParty } from '../common/Icons';
import { useNavigate } from 'react-router-dom';
import { publicAPI } from '../../services/api';
import { initPixel, trackPixel } from '../../utils/pixel';

const FEATURES = [
  { img: '/landing/f-orders.jpg', title: 'Multi-Channel Orders', desc: 'Sync Shopify and custom channels into one unified dashboard — no manual entry, no spreadsheets.' },
  { img: '/landing/f-courier.jpg', title: 'Ship With Any Courier', desc: 'Integrate multiple couriers — book, print labels and auto-track. You are never locked to one carrier.' },
  { img: '/landing/f-profit.jpg', title: 'Real-Time Profit', desc: 'See true margin per order — shipping cost, COD fees and RTO losses netted automatically.' },
  { img: '/landing/f-stock.jpg', title: 'Multi-Location Stock', desc: 'Live inventory across every warehouse. Auto-deduct on dispatch, restore on return — always accurate.' },
  { img: '/landing/f-purchase.jpg', title: 'Purchase & Vendor', desc: 'Raise POs, track buying price, manage vendor dues and record payments — end to end.' },
  { img: '/landing/f-rto.jpg', title: 'RTO Reconcile', desc: 'Scan returns into per-location batches, restock or buyback, and keep stock counts exact.' },
];

const STEPS = [
  { n: '1', title: 'Connect your store', desc: 'Link Shopify securely in a couple of clicks.' },
  { n: '2', title: 'Everything syncs', desc: 'Orders, products, stock and customers flow in automatically.' },
  { n: '3', title: 'Run & profit', desc: 'Ship with any courier, manage stock, purchases, returns — and watch real profit.' },
];

const CAPS = ['Multi-Courier', 'Real-Time Profit', 'Multi-Location Stock', 'Purchase & Vendors', 'RTO Reconcile', 'Payments & Remittance'];

const COMPARE = [
  ['Not locked to one courier — integrate many', true, 'Often single courier or limited'],
  ['Real-time profit (COD, RTO & shipping netted)', true, 'Rarely — you calculate manually'],
  ['Multi-location stock, auto-accurate', true, 'Basic or single location'],
  ['Purchase, vendor & payments built in', true, 'Separate tools / subscriptions'],
  ['RTO reconcile with parcel scanning', true, 'Not available'],
  ['One platform, simple pay-as-you-go', true, 'Multiple monthly plans stack up'],
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const openDemo = () => { trackPixel('InitiateCheckout', { content_name: 'Book a Demo' }); setShowDemo(true); };

  useEffect(() => { initPixel(); }, []); // Meta Pixel PageView

  return (
    <div style={s.page}>
      <style>{`
        @keyframes mcPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.5); } 50% { box-shadow: 0 0 0 12px rgba(37,99,235,0); } }
        @keyframes mcFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .mc-demo-btn { animation: mcPulse 2.2s infinite; transition: transform .15s ease; }
        .mc-demo-btn:hover { transform: translateY(-2px) scale(1.03); animation: none; }
        .mc-feat:hover { transform: translateY(-6px); box-shadow: 0 24px 50px rgba(37,99,235,0.14); }
        .mc-float { animation: mcFloat 6s ease-in-out infinite; }

        /* ── Tablet / mobile ── */
        @media (max-width: 768px) {
          .mc-navinner { padding: 0 14px !important; height: 56px !important; }
          .mc-navinner img { height: 24px !important; }
          .mc-nav-login { display: none !important; }
          .mc-nav-demo { padding: 7px 13px !important; font-size: 13px !important; }
          .mc-nav-get { padding: 8px 14px !important; font-size: 13px !important; }
          .mc-heroinner { padding: 46px 18px 54px !important; gap: 30px !important; }
          .mc-h1 { font-size: 34px !important; }
          .mc-hsub { font-size: 16px !important; }
          .mc-h2 { font-size: 27px !important; }
          .mc-secinner { padding: 0 16px !important; }
          .mc-section { padding: 58px 0 !important; }
          .mc-cta { padding: 64px 18px !important; }
          .mc-ctatitle { font-size: 28px !important; }
          .mc-cmp-table { padding: 14px !important; }
          .mc-cmp-mc { width: 66px !important; font-size: 10.5px !important; overflow-wrap: anywhere; line-height: 1.25; }
          .mc-cmp-other { width: 92px !important; font-size: 10.5px !important; overflow-wrap: anywhere; line-height: 1.25; }
          .mc-cmp-label { font-size: 12.5px !important; padding-right: 8px !important; }
          /* keep the ✓/✕ ticks readable even though the column font shrank */
          .mc-cmp-tick { font-size: 14px !important; }
          .mc-cmp-note { font-size: 10px !important; }
        }
        @media (max-width: 480px) {
          .mc-h1 { font-size: 28px !important; }
          .mc-hsub { font-size: 15px !important; }
          .mc-h2 { font-size: 23px !important; }
          .mc-ctatitle { font-size: 23px !important; }
          .mc-heroctas { width: 100%; }
          .mc-heroctas button, .mc-cta-btns button { flex: 1 1 100% !important; width: 100% !important; }
        }
      `}</style>

      {/* Navbar */}
      <nav style={s.nav}>
        <div className="mc-navinner" style={s.navInner}>
          <img src="/logo-white.png" alt="ModCommerce" style={{ height: 30, objectFit: 'contain' }} />
          <div style={s.navRight}>
            <button className="mc-nav-login" style={s.navLink} onClick={() => navigate('/user')}>Log in</button>
            <button className="mc-demo-btn mc-nav-demo" style={s.demoNavBtn} onClick={openDemo}>Book a Demo</button>
            <button className="mc-nav-get" style={s.getStartedBtn} onClick={() => navigate('/user')}>Get Started →</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={s.hero}>
        <div style={s.heroGlow} />
        <div style={s.heroGrid} />
        <div className="mc-heroinner" style={s.heroInner}>
          <div style={s.heroText}>
            <div style={s.heroBadge}>● The Operating System for Modern Commerce</div>
            <h1 className="mc-h1" style={s.heroTitle}>
              The Operating System for <span style={s.heroAccent}>Modern Commerce</span>
            </h1>
            <p className="mc-hsub" style={s.heroSub}>
              Manage orders, maximize profits, and streamline every commerce operation —
              from one intelligent platform.
            </p>
            <div className="mc-heroctas" style={s.heroCtas}>
              <button style={s.ctaPrimary} onClick={() => navigate('/user')}>Get Started</button>
              <button className="mc-demo-btn" style={s.ctaDemo} onClick={openDemo}>Book a Demo</button>
            </div>
            <div style={s.caps}>
              {CAPS.map(c => <span key={c} style={s.capChip}>{c}</span>)}
            </div>
          </div>
          <div style={s.heroVisual}>
            <img className="mc-float" src="/landing/hero-new.png" alt="ModCommerce platform" style={s.heroImg} />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mc-section" style={s.features}>
        <div className="mc-secinner" style={s.sectionInner}>
          <div style={s.sectionBadge}>One platform</div>
          <h2 className="mc-h2" style={s.sectionTitle}>Everything your commerce runs on</h2>
          <p style={s.sectionSub}>From order intake to profit — every operation in one place.</p>
          <div style={s.featuresGrid}>
            {FEATURES.map(f => (
              <div key={f.title} className="mc-feat" style={s.featureCard}>
                <div style={s.featureImgWrap}>
                  <img src={f.img} alt={f.title} style={s.featureImg} />
                </div>
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="mc-section" style={s.compare}>
        <div style={s.compareGlow} />
        <div className="mc-secinner" style={s.sectionInner}>
          <div style={{ ...s.sectionBadge, background: 'rgba(96,165,250,0.15)', color: '#93c5fd', border: '1px solid rgba(96,165,250,0.3)' }}>Why ModCommerce</div>
          <h2 className="mc-h2" style={{ ...s.sectionTitle, color: '#fff' }}>One platform vs a pile of apps</h2>
          <p style={{ ...s.sectionSub, color: 'rgba(255,255,255,0.7)' }}>Stop stitching tools together. Run it all — and actually see your profit.</p>
          <div style={s.compareWrap}>
            <div style={s.compareVisual}>
              <img className="mc-float" src="/landing/compare.jpg" alt="ModCommerce vs scattered tools" style={s.compareImg} />
            </div>
            <div className="mc-cmp-table" style={s.compareTable}>
              <div style={s.compareHeadRow}>
                <span style={{ flex: 1 }} />
                <span className="mc-cmp-mc" style={s.compareColMc}>ModCommerce</span>
                <span className="mc-cmp-other" style={s.compareColOther}>Other apps</span>
              </div>
              {COMPARE.map(([label, , other]) => (
                <div key={label} style={s.compareRow}>
                  <span className="mc-cmp-label" style={s.compareLabel}>{label}</span>
                  <span className="mc-cmp-mc" style={s.compareColMc}><span className="mc-cmp-tick" style={s.tickYes}>✓</span></span>
                  <span className="mc-cmp-other" style={s.compareColOther}><span className="mc-cmp-tick" style={s.tickNo}>✕</span><span className="mc-cmp-note" style={s.otherNote}>{other}</span></span>
                </div>
              ))}
              <button className="mc-demo-btn" style={{ ...s.ctaDemo, marginTop: 18, width: '100%' }} onClick={openDemo}>See it live — Book a Demo</button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mc-section" style={s.steps}>
        <div className="mc-secinner" style={s.sectionInner}>
          <div style={s.sectionBadge}>How it works</div>
          <h2 className="mc-h2" style={s.sectionTitle}>Live in minutes</h2>
          <p style={s.sectionSub}>No complex setup. Connect once and you're running.</p>
          <div style={s.stepsGrid}>
            {STEPS.map((st, i) => (
              <div key={st.n} style={s.stepCard}>
                <div style={s.stepNum}>{st.n}</div>
                <h3 style={s.stepTitle}>{st.title}</h3>
                <p style={s.stepDesc}>{st.desc}</p>
                {i < STEPS.length - 1 && <div style={s.stepArrow}>→</div>}
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button style={{ ...s.pricingLink }} onClick={() => navigate('/pricing')}>See pay-as-you-go pricing →</button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mc-cta" style={s.ctaSection}>
        <div style={s.ctaGlow} />
        <div style={s.ctaInner}>
          <h2 className="mc-ctatitle" style={s.ctaTitle}>Run your entire commerce from one dashboard</h2>
          <p style={s.ctaSub}>Get started free, or book a 1-on-1 demo and we'll show you how.</p>
          <div className="mc-cta-btns" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={s.ctaBigBtn} onClick={() => navigate('/user')}>Get Started →</button>
            <button className="mc-demo-btn" style={s.ctaBigDemo} onClick={openDemo}>Book a Demo</button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <div style={s.footerTop}>
          <div style={{ flex: '1 1 320px' }}>
            <img src="/logo-white.png" alt="ModCommerce" style={{ height: 24, objectFit: 'contain', marginBottom: 12 }} />
            <p style={s.footerText}>ModCommerce is a product of NSDVIBE CREATIONS LLP.</p>
            <p style={s.footerText}>14N, Centre Sinthee Road, Beside Centre Sinthee Club, Kolkata, West Bengal, India, 700050</p>
            <p style={s.footerText}>GSTIN: 19AAYFN0815C1Z5 · <a href="mailto:support@modcommerce.in" style={s.footerA}>support@modcommerce.in</a></p>
          </div>
          <div style={s.footerLinks}>
            {[['/pricing', 'Pricing'], ['/privacy', 'Privacy Policy'], ['/terms', 'Terms & Conditions'], ['/dpa', 'Data Processing Addendum'], ['/refund', 'Refund & Cancellation'], ['/shipping', 'Shipping & Delivery'], ['/contact', 'Contact Us']].map(([to, label]) => (
              <a key={to} href={to} style={s.footerLink}>{label}</a>
            ))}
          </div>
        </div>
        <div style={s.footerBottom}>© {new Date().getFullYear()} NSDVIBE CREATIONS LLP. All rights reserved.</div>
      </footer>

      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}
    </div>
  );
}

const ORDER_OPTIONS = ['0-100', '100-500', '500-1000', '1000+'];
const DEMO_HIGHLIGHTS = ['Ship with any courier', 'Track real-time profit', 'Multi-location stock & purchases', 'RTO reconcile & returns'];

function DemoModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', orders_range: '', consent: false, website: '', hp: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [openedAt] = useState(() => Date.now());
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr('');
    // Honeypot: real users never see/fill this. If filled → bot. Fake success, send nothing.
    if (form.hp) { setDone(true); return; }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return setErr('Please fill name, email and phone.');
    if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\D/g, ''))) return setErr('Enter a valid 10-digit mobile number.');
    if (!form.website.trim()) return setErr('Please add your website or Instagram.');
    if (!form.orders_range) return setErr('Select your monthly order volume.');
    if (!form.consent) return setErr('Please accept the policy to continue.');
    setBusy(true);
    try {
      const r = await publicAPI.demoLead({
        name: form.name, email: form.email, phone: form.phone.replace(/\D/g, ''),
        orders_range: form.orders_range, consent: form.consent, website: form.website,
        hp: form.hp, form_ms: Date.now() - openedAt,
      });
      trackPixel('Lead', { content_name: 'Book a Demo', orders_range: form.orders_range }, r.data && r.data.event_id);
      setDone(true);
    }
    catch (e) { setErr(e.response?.data?.error || 'Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={dm.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dm.modal}>
        <button style={dm.close} onClick={onClose}>✕</button>
        {done ? (
          <div style={{ textAlign: 'center', padding: '30px 6px' }}>
            <div style={{ color: '#16a34a' }}><IcoParty size={42} /></div>
            <h3 style={dm.title}>Request received!</h3>
            <p style={dm.sub}>Our team will reach out shortly to schedule your demo.</p>
            <button style={dm.submit} onClick={onClose}>Done</button>
          </div>
        ) : (
          <div style={dm.grid}>
            <div style={dm.left}>
              <div style={dm.badge}>● Free 1-on-1 Demo</div>
              <h3 style={dm.title}>Book a Demo</h3>
              <p style={dm.sub}>See how ModCommerce runs your entire commerce from one dashboard.</p>
              <div style={{ marginTop: 16 }}>
                {DEMO_HIGHLIGHTS.map(h => <div key={h} style={dm.hi}><span style={dm.tick}>✓</span> {h}</div>)}
              </div>
            </div>
            <div style={dm.right}>
              {/* Honeypot — hidden from users, catches bots. Do not remove. */}
              <input tabIndex={-1} autoComplete="off" value={form.hp} onChange={e => set('hp', e.target.value)}
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
              <input style={dm.input} placeholder="Full name" value={form.name} onChange={e => set('name', e.target.value)} />
              <input style={dm.input} placeholder="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              <div style={dm.phoneWrap}>
                <span style={dm.phonePrefix}>+91</span>
                <input style={dm.phoneInput} type="tel" inputMode="numeric" maxLength={10} placeholder="9876543210"
                  value={form.phone} onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} />
              </div>
              <input style={dm.input} placeholder="Website or Instagram (e.g. @yourbrand)" value={form.website} onChange={e => set('website', e.target.value)} />
              <div style={dm.lbl}>Orders you ship per month</div>
              <div style={dm.opts}>
                {ORDER_OPTIONS.map(o => (
                  <button key={o} onClick={() => set('orders_range', o)} style={{ ...dm.opt, ...(form.orders_range === o ? dm.optOn : {}) }}>{o}</button>
                ))}
              </div>
              <label style={dm.consent}>
                <input type="checkbox" checked={form.consent} onChange={e => set('consent', e.target.checked)} />
                <span>I agree to be contacted and accept the <a href="/privacy" target="_blank" rel="noreferrer" style={dm.link}>Privacy Policy</a>.</span>
              </label>
              {err && <div style={dm.err}>{err}</div>}
              <button className="mc-demo-btn" style={dm.submit} disabled={busy} onClick={submit}>{busy ? 'Submitting…' : 'Request Demo →'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { background: '#ffffff', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#0f172a' },

  nav: { position: 'sticky', top: 0, background: 'rgba(9,14,27,0.72)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)', zIndex: 100 },
  navInner: { maxWidth: 1180, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navRight: { display: 'flex', alignItems: 'center', gap: 10 },
  navLink: { background: 'none', border: 'none', color: '#cbd5e1', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 12px' },
  demoNavBtn: { background: '#fff', color: '#1e40af', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  getStartedBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 6px 18px rgba(37,99,235,0.4)' },

  // Hero (dark, futuristic)
  hero: { position: 'relative', overflow: 'hidden', background: 'radial-gradient(1200px 600px at 70% -10%, #16225a 0%, #0b1020 45%, #090e1b 100%)' },
  heroGlow: { position: 'absolute', top: -160, left: -120, width: 620, height: 620, background: 'radial-gradient(circle, rgba(37,99,235,0.28) 0%, rgba(37,99,235,0) 70%)', pointerEvents: 'none' },
  heroGrid: { position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '46px 46px', maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)', pointerEvents: 'none' },
  heroInner: { position: 'relative', maxWidth: 1180, margin: '0 auto', padding: '80px 24px 92px', display: 'flex', alignItems: 'center', gap: 52, flexWrap: 'wrap' },
  heroText: { flex: '1 1 440px' },
  heroBadge: { display: 'inline-block', background: 'rgba(96,165,250,0.12)', color: '#93c5fd', borderRadius: 30, padding: '6px 16px', fontSize: 13, fontWeight: 600, marginBottom: 22, border: '1px solid rgba(96,165,250,0.28)' },
  heroTitle: { fontSize: 54, fontWeight: 800, lineHeight: 1.08, margin: '0 0 20px', letterSpacing: '-0.02em', color: '#fff' },
  heroAccent: { background: 'linear-gradient(90deg,#60a5fa,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSub: { color: 'rgba(255,255,255,0.72)', fontSize: 19, lineHeight: 1.6, marginBottom: 32, maxWidth: 540 },
  heroCtas: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 30 },
  ctaPrimary: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, padding: '15px 32px', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 12px 30px rgba(37,99,235,0.45)' },
  ctaDemo: { background: '#fff', color: '#1e40af', border: 'none', borderRadius: 12, padding: '15px 28px', fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  caps: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  capChip: { fontSize: 12.5, fontWeight: 600, color: '#cbd5e1', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '6px 12px' },
  heroVisual: { flex: '1 1 420px', display: 'flex', justifyContent: 'center' },
  heroImg: { width: '100%', maxWidth: 560, borderRadius: 20, boxShadow: '0 40px 90px rgba(37,99,235,0.3)' },

  sectionInner: { maxWidth: 1180, margin: '0 auto', padding: '0 24px', textAlign: 'center' },
  sectionBadge: { display: 'inline-block', background: '#eff4ff', color: '#2563eb', borderRadius: 30, padding: '6px 16px', fontSize: 13, fontWeight: 600, marginBottom: 16, border: '1px solid #dbe6ff' },
  sectionTitle: { fontSize: 38, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.02em' },
  sectionSub: { color: '#64748b', fontSize: 17, marginBottom: 48 },

  features: { padding: '90px 0', background: '#fff' },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, textAlign: 'left' },
  featureCard: { background: '#fff', border: '1px solid #eef2f7', borderRadius: 18, overflow: 'hidden', boxShadow: '0 8px 30px rgba(15,23,42,0.05)', transition: 'transform var(--mc-t-base) var(--mc-ease), box-shadow var(--mc-t-base) var(--mc-ease)' },
  featureImgWrap: { background: 'linear-gradient(180deg, #f4f8ff 0%, #eef4ff 100%)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 },
  featureImg: { width: 160, height: 160, objectFit: 'contain' },
  featureTitle: { fontSize: 18, fontWeight: 700, margin: '20px 22px 8px' },
  featureDesc: { color: '#64748b', fontSize: 14, lineHeight: 1.6, margin: '0 22px 24px' },

  // Comparison (dark)
  compare: { position: 'relative', overflow: 'hidden', padding: '92px 0', background: 'radial-gradient(900px 500px at 20% 0%, #16225a 0%, #0b1020 55%, #090e1b 100%)' },
  compareGlow: { position: 'absolute', bottom: -150, right: -120, width: 560, height: 560, background: 'radial-gradient(circle, rgba(167,139,250,0.22) 0%, rgba(167,139,250,0) 70%)', pointerEvents: 'none' },
  compareWrap: { position: 'relative', display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap', textAlign: 'left' },
  compareVisual: { flex: '1 1 360px', display: 'flex', justifyContent: 'center' },
  compareImg: { width: '100%', maxWidth: 480, borderRadius: 18, boxShadow: '0 30px 70px rgba(0,0,0,0.4)' },
  compareTable: { flex: '1 1 420px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, padding: 20 },
  compareHeadRow: { display: 'flex', alignItems: 'center', padding: '6px 8px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  compareColMc: { width: 108, textAlign: 'center', color: '#93c5fd', fontWeight: 800, fontSize: 13, flexShrink: 0 },
  compareColOther: { width: 150, textAlign: 'center', color: '#94a3b8', fontWeight: 700, fontSize: 13, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  compareRow: { display: 'flex', alignItems: 'center', padding: '13px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  compareLabel: { flex: 1, color: '#e2e8f0', fontSize: 13.5, fontWeight: 500, paddingRight: 10 },
  tickYes: { color: '#22c55e', fontWeight: 800, fontSize: 17 },
  tickNo: { color: '#ef4444', fontWeight: 800, fontSize: 15 },
  otherNote: { color: '#64748b', fontSize: 10.5, lineHeight: 1.3 },

  steps: { padding: '90px 0', background: '#f8fafc', borderTop: '1px solid #eef2f7', borderBottom: '1px solid #eef2f7' },
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, textAlign: 'center' },
  stepCard: { position: 'relative', background: '#fff', border: '1px solid #eef2f7', borderRadius: 18, padding: '36px 24px', boxShadow: '0 8px 30px rgba(15,23,42,0.05)' },
  stepNum: { width: 48, height: 48, margin: '0 auto 18px', borderRadius: 14, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: '#fff', fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(37,99,235,0.35)' },
  stepTitle: { fontSize: 18, fontWeight: 700, margin: '0 0 8px' },
  stepDesc: { color: '#64748b', fontSize: 14, lineHeight: 1.6, margin: 0 },
  stepArrow: { position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1', fontSize: 24, fontWeight: 700, zIndex: 2 },
  pricingLink: { background: 'none', border: '1px solid #cbd5e1', color: '#2563eb', borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },

  ctaSection: { position: 'relative', overflow: 'hidden', padding: '96px 24px', textAlign: 'center', background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 55%, #7c3aed 100%)' },
  ctaGlow: { position: 'absolute', top: -140, left: '40%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)', pointerEvents: 'none' },
  ctaInner: { position: 'relative', maxWidth: 680, margin: '0 auto' },
  ctaTitle: { fontSize: 40, fontWeight: 800, margin: '0 0 12px', color: '#fff', letterSpacing: '-0.02em' },
  ctaSub: { color: 'rgba(255,255,255,0.9)', fontSize: 17, marginBottom: 30 },
  ctaBigBtn: { background: '#0b1020', color: '#fff', border: 'none', borderRadius: 12, padding: '16px 34px', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 12px 30px rgba(0,0,0,0.25)' },
  ctaBigDemo: { background: '#fff', color: '#1e40af', border: 'none', borderRadius: 12, padding: '16px 34px', fontSize: 16, fontWeight: 700, cursor: 'pointer' },

  footer: { background: '#0b1020', padding: '44px 24px 24px' },
  footerTop: { maxWidth: 1180, margin: '0 auto', display: 'flex', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' },
  footerText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.7, margin: '0 0 4px' },
  footerA: { color: '#60a5fa', textDecoration: 'none' },
  footerLinks: { display: 'flex', flexDirection: 'column', gap: 8 },
  footerLink: { color: '#cbd5e1', fontSize: 13, textDecoration: 'none' },
  footerBottom: { maxWidth: 1180, margin: '24px auto 0', paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', color: '#475569', fontSize: 12 },
};

const dm = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(9,14,27,0.66)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 },
  modal: { position: 'relative', background: '#fff', borderRadius: 20, width: '100%', maxWidth: 720, boxShadow: '0 30px 80px rgba(0,0,0,0.4)', overflow: 'hidden' },
  close: { position: 'absolute', top: 14, right: 16, background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer', zIndex: 2 },
  grid: { display: 'flex', flexWrap: 'wrap' },
  left: { flex: '1 1 260px', background: 'radial-gradient(500px 300px at 20% 0%, #2b3a8f 0%, #1e3a8a 40%, #1e40af 100%)', color: '#fff', padding: '34px 28px' },
  badge: { display: 'inline-block', background: 'rgba(255,255,255,0.15)', borderRadius: 30, padding: '5px 14px', fontSize: 12, fontWeight: 700, marginBottom: 16 },
  title: { fontSize: 26, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.02em' },
  sub: { color: 'rgba(255,255,255,0.85)', fontSize: 14, lineHeight: 1.6, margin: 0 },
  hi: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, fontWeight: 600, margin: '11px 0' },
  tick: { background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 },
  right: { flex: '1 1 320px', padding: '30px 28px' },
  input: { width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 15, marginBottom: 10, boxSizing: 'border-box' },
  phoneWrap: { display: 'flex', alignItems: 'stretch', border: '1px solid #cbd5e1', borderRadius: 10, overflow: 'hidden', marginBottom: 10 },
  phonePrefix: { display: 'flex', alignItems: 'center', padding: '0 13px', background: '#f1f5f9', color: '#475569', fontSize: 15, fontWeight: 600, borderRight: '1px solid #cbd5e1' },
  phoneInput: { flex: 1, border: 'none', padding: '12px 14px', fontSize: 15, outline: 'none', width: '100%' },
  lbl: { fontSize: 12.5, fontWeight: 700, color: '#475569', margin: '6px 0 8px' },
  opts: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 },
  opt: { background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 10, padding: '10px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  optOn: { background: '#eff6ff', borderColor: '#2563eb', color: '#2563eb' },
  consent: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: '#64748b', lineHeight: 1.5, marginBottom: 14, cursor: 'pointer' },
  link: { color: '#2563eb', fontWeight: 600 },
  err: { background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 },
  submit: { width: '100%', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};
